import type { MediaPostCategoryFilter } from "@/contexts/feed-context";
import { PostCategoryFilter } from "@/contexts/feed-context";
import type { PostFilter } from "@/graphql/codegen/graphql";

const hasNonEmptyFilterValue = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (value && typeof value === "object") {
    return Object.values(value).some((entry) => hasNonEmptyFilterValue(entry));
  }

  return value !== undefined;
};

export const getFeedFilters = ({
  userPublicKey,
  postCategoryFilter,
}: {
  userPublicKey?: string;
  date?: Date | string;
  postCategoryFilter: Record<PostCategoryFilter, boolean>;
}) => {
  const withRepliesFilter: PostFilter | undefined = !postCategoryFilter[
    PostCategoryFilter.WithReplies
  ]
    ? {
        parentPostHash: {
          isNull: true,
        },
      }
    : undefined;

  if (!userPublicKey) return withRepliesFilter;

  const gatedPostsFilter = {
    subscriptionAccessGroupExists:
      !!postCategoryFilter[PostCategoryFilter.GatedPosts],
  };

  const filters: PostFilter = {
    ...withRepliesFilter,
    ...(!postCategoryFilter[PostCategoryFilter.GatedPosts]
      ? gatedPostsFilter
      : {}),
    isHidden: {
      equalTo: false,
    },

    poster: {
      profile: {
        publicKey: {
          equalTo: userPublicKey,
        },
      },
    },

    or: [
      postCategoryFilter[PostCategoryFilter.GatedPosts]
        ? gatedPostsFilter
        : null,
      postCategoryFilter.Repost
        ? {
            and: [
              {
                repostedPostExists: true,
              },
              {
                isQuotedRepost: {
                  equalTo: false,
                },
              },
            ],
          }
        : null,
      postCategoryFilter.QuotedRepost
        ? {
            isQuotedRepost: {
              equalTo: true,
            },
          }
        : null,
      postCategoryFilter.Post
        ? {
            and: [
              {
                repostedPostExists: false,
              },
              {
                isQuotedRepost: {
                  equalTo: false,
                },
              },
            ],
          }
        : null,
    ].filter(Boolean) as PostFilter[],
  };

  return hasNonEmptyFilterValue(filters) ? filters : undefined;
};

export const getMediaFeedFilters = ({
  userPublicKey,
  mediaPostCategoryFilter,
}: {
  userPublicKey?: string;
  mediaPostCategoryFilter: Record<MediaPostCategoryFilter, boolean>;
}) => {
  const withRepliesFilter = {
    parentPostHash: {
      isNull: true,
    },
  };

  if (!userPublicKey) return withRepliesFilter;

  const videoFilter = {
    videoUrls: {
      isNull: false,
    },
  };
  const imgFilter = {
    imageUrls: {
      isNull: false,
    },
  };

  const filters: PostFilter = {
    ...withRepliesFilter,
    isHidden: {
      equalTo: false,
    },

    poster: {
      profile: {
        publicKey: {
          equalTo: userPublicKey,
        },
      },
    },
    or: [
      mediaPostCategoryFilter.VideoUrls ? videoFilter : null,
      mediaPostCategoryFilter.ImageUrls ? imgFilter : null,
    ].filter(Boolean) as PostFilter[],
  };

  if (filters.or && !filters.or.length) {
    filters.or?.push(videoFilter, imgFilter);
  }

  return filters;
};
